<script lang="ts">
  import { m } from '$lib/paraglide/messages';
  import { translation } from '$lib/locale/locale';
  import type { Tag } from '$lib/models/tag';
  import { createEventDispatcher } from 'svelte';
  import collapseIcon from '$lib/assets/search/collapse.svg';
  import DoubleRangeSlider from '$lib/ui/shared/double-range-slider.svelte';
  import { filterCount, findSelectedTags, isAnyRemovableFilterSelected } from '$lib/utils/search';
  import { CategoryType } from '$lib/utils/category';
  import CategoryFilterGroup from './category-filter-group.svelte';
  import { AssetType } from '$lib/utils/asset';
  import BottomSheet from '$lib/ui/shared/bottom-sheet.svelte';
  import { slide } from 'svelte/transition';
  import { defaultFilters, Filters } from '$lib/models/filters';
  import SearchSelectedFilters from './search-selected-filters.svelte';

  export let type: AssetType;
  export let moods: Tag[] = [];
  export let genres: Tag[] = [];
  export let energyLevels: Tag[] = [];
  export let instruments: Tag[] = [];
  export let themes: Tag[] = [];
  export let types: Tag[] = [];
  export let categories: Tag[] = [];
  export let minBpm: number = defaultFilters.minBpm;
  export let maxBpm: number = defaultFilters.maxBpm;
  export let minLength: number = defaultFilters.minLength;
  export let maxLength: number = defaultFilters.maxLength;
  export let orderBy: string = defaultFilters.orderBy;
  export let initialFilters: Filters;
  export let showBpm = true;
  export let showLength = true;
  export let initialExpanded = '';
  export let query = '';
  export let showFiltersAsLinks = true;
  export let showFiltersSheet = false;
  export let filtersCount = 0;

  const {} = translation();

  const dispatch = createEventDispatcher<{ change: Filters; removeQuery: void }>();

  let expanded: string = '';
  let timer: any;
  let filtering = false;
  let selectedMinBpm = minBpm;
  let selectedMaxBpm = maxBpm;
  let selectedMinLength = minLength;
  let selectedMaxLength = maxLength;
  let selectedMoods: Tag[] = [];
  let selectedGenres: Tag[] = [];
  let selectedEnergyLevels: Tag[] = [];
  let selectedInstruments: Tag[] = [];
  let selectedThemes: Tag[] = [];
  let selectedTypes: Tag[] = [];
  let selectedCategories: Tag[] = [];
  let allSelectedTags: Tag[] = [];
  let selectedSimilarTo: number = 0;

  /**
   * Configures the initial state of the filters (selected tags, bpm, length, etc.)
   */
  function initFilters() {
    selectedMoods = findSelectedTags(moods, initialFilters.moods);
    selectedGenres = findSelectedTags(genres, initialFilters.genres);
    selectedEnergyLevels = findSelectedTags(energyLevels, initialFilters.energyLevels);
    selectedInstruments = findSelectedTags(instruments, initialFilters.instruments);
    selectedThemes = findSelectedTags(themes, initialFilters.themes);
    selectedTypes = findSelectedTags(types, initialFilters.types);
    selectedCategories = findSelectedTags(categories, initialFilters.categories);

    allSelectedTags = [
      ...(selectedMoods || []),
      ...(selectedGenres || []),
      ...(selectedEnergyLevels || []),
      ...(selectedInstruments || []),
      ...(selectedThemes || []),
      ...(selectedTypes || []),
      ...(selectedCategories || []),
    ];

    moods = moods;
    genres = genres;
    energyLevels = energyLevels;
    instruments = instruments;
    themes = themes;
    types = types;
    categories = categories;
    selectedMinBpm = initialFilters.minBpm;
    selectedMaxBpm = initialFilters.maxBpm;
    selectedMinLength = initialFilters.minLength;
    selectedMaxLength = initialFilters.maxLength;
    selectedSimilarTo = initialFilters.similarTo;
    orderBy = initialFilters.orderBy;

    filtering = isAnyRemovableFilterSelected(initialFilters);
    filtersCount = filterCount(initialFilters);
  }

  /**
   * Expands a filter section tab.
   */
  function expand(name: string) {
    expanded = expanded == name ? '' : name;
  }

  /**
   * Opens the mobile filters bottom sheet.
   */
  function openFiltersSheet() {
    expanded = '';
    showFiltersSheet = true;
  }

  /**
   * Closes the mobile filters bottom sheet and impacts the changes made to the filters.
   */
  function closeFiltersSheet() {
    showFiltersSheet = false;
    expanded = '';
  }

  /**
   * Selects a specific BPM range and notifies of the change.
   */
  function selectBpm(min: number, max: number) {
    selectedMinBpm = min;
    selectedMaxBpm = max;
    onFilterChanged();
  }

  /**
   * Removes the selected BPM range and notifies of the change.
   */
  function removeBpm() {
    selectedMinBpm = minBpm;
    selectedMaxBpm = maxBpm;
    onFilterChanged();
  }

  /**
   * Selects a specific track length (duration) range and notifies of the change.
   */
  function selectLength(min: number, max: number) {
    selectedMinLength = min;
    selectedMaxLength = max;
    onFilterChanged();
  }

  /**
   * Removes the selected track length (duration) range and notifies of the change.
   */
  function removeLength() {
    selectedMinLength = minLength;
    selectedMaxLength = maxLength;
    onFilterChanged();
  }

  function removeSimilarTo() {
    selectedSimilarTo = 0;
    onFilterChanged();
  }

  /**
   * Removes the selected search query string and notifies of the change.
   */
  function removeQuery() {
    query = '';
    dispatch('removeQuery');
  }

  /**
   * Selects a order by and notifies of the change.
   */
  export function setOrderBy(value: string) {
    orderBy = value;
    onFilterChanged();
  }

  /**
   * Removes all active filters, including order by and the search query.
   */
  function removeAllFilters() {
    moods?.forEach((m) => (m.selected = false));
    genres?.forEach((g) => (g.selected = false));
    energyLevels?.forEach((e) => (e.selected = false));
    instruments?.forEach((i) => (i.selected = false));
    themes?.forEach((t) => (t.selected = false));
    types?.forEach((t) => (t.selected = false));
    categories?.forEach((c) => {
      c.selected = false;
      c.children?.forEach((cc) => (cc.selected = false));
    });
    selectedMoods = [];
    selectedGenres = [];
    selectedEnergyLevels = [];
    selectedInstruments = [];
    selectedThemes = [];
    selectedTypes = [];
    selectedCategories = [];
    selectedMinBpm = minBpm;
    selectedMaxBpm = maxBpm;
    selectedMinLength = minLength;
    selectedMaxLength = maxLength;
    selectedSimilarTo = 0;
    orderBy = defaultFilters.orderBy;

    moods = moods;
    genres = genres;
    energyLevels = energyLevels;
    instruments = instruments;
    themes = themes;
    types = types;
    categories = categories;
    minBpm = minBpm;
    maxBpm = maxBpm;

    if (query) {
      removeQuery();
    }
    onFilterChanged();
  }

  /**
   * Notifies the parent component that the filters have changed.
   * This has a 100ms debounce to avoid calling it too often.
   */
  function onFilterChanged() {
    clearTimeout(timer);
    timer = setTimeout(() => {
      updateSelected();
      const filters = {
        moods: selectedMoods,
        genres: selectedGenres,
        categories: selectedCategories,
        energyLevels: selectedEnergyLevels,
        instruments: selectedInstruments,
        themes: selectedThemes,
        types: selectedTypes,
        minBpm: selectedMinBpm,
        maxBpm: selectedMaxBpm,
        minLength: selectedMinLength,
        maxLength: selectedMaxLength,
        similarTo: selectedSimilarTo,
        orderBy,
      };
      filtering = isAnyRemovableFilterSelected(filters);
      dispatch('change', filters);
      filtersCount = filterCount(filters);
    }, 100);
  }

  function updateSelected() {
    moods = moods;
    genres = genres;
    categories = categories;
    selectedMoods = findSelectedTags(moods);
    selectedGenres = findSelectedTags(genres);
    selectedEnergyLevels = findSelectedTags(energyLevels);
    selectedInstruments = findSelectedTags(instruments);
    selectedThemes = findSelectedTags(themes);
    selectedTypes = findSelectedTags(types);
    selectedCategories = findSelectedTags(categories);
    allSelectedTags = [
      ...selectedMoods,
      ...selectedGenres,
      ...selectedEnergyLevels,
      ...selectedInstruments,
      ...selectedThemes,
      ...selectedTypes,
      ...selectedCategories,
    ];
  }

  function clearFilterType(type: string) {
    if (type === 'moods') {
      moods.forEach((m) => (m.selected = false));
      selectedMoods = [];
    } else if (type === 'genres') {
      genres.forEach((g) => (g.selected = false));
      selectedGenres = [];
    } else if (type === 'energyLevels') {
      energyLevels.forEach((e) => (e.selected = false));
      selectedEnergyLevels = [];
    } else if (type === 'instruments') {
      instruments.forEach((i) => (i.selected = false));
      selectedInstruments = [];
    } else if (type === 'themes') {
      themes.forEach((t) => (t.selected = false));
      selectedThemes = [];
    } else if (type === 'types') {
      types.forEach((t) => (t.selected = false));
      selectedTypes = [];
    } else if (type === 'categories') {
      categories.forEach((c) => (c.selected = false));
      selectedCategories = [];
    }
    onFilterChanged();
  }

  // When the component is updated, we set the initial filters and expanded filter section.
  $: initialFilters, initFilters();
  $: expanded = initialExpanded;
</script>

<aside class="sidebar">
  <!-- Filters section -->
  <div class="filters-section">
    <div class="filters-header">
      <h3>
        {m['search.filters']()}
        {#if filtersCount > 0}
          <span class="total-count">({filtersCount})</span>
        {/if}
      </h3>
      {#if filtering || query}
        <button class="clear-all-btn" on:click={removeAllFilters} aria-label={m['search.reset-filters']()}>
          {m['actions.clear-all']()}
        </button>
      {/if}
    </div>

    {#if filtering || query}
      <SearchSelectedFilters
        selectedCategories={allSelectedTags}
        {minBpm}
        {maxBpm}
        {minLength}
        {maxLength}
        {selectedMinBpm}
        {selectedMaxBpm}
        {selectedMinLength}
        {selectedMaxLength}
        {selectedSimilarTo}
        {query}
        on:categoryChange={onFilterChanged}
        on:removeBpm={removeBpm}
        on:removeLength={removeLength}
        on:removeAll={removeAllFilters}
        on:removeQuery={removeQuery}
        on:removeSimilarTo={removeSimilarTo}
      />
    {/if}

    <!-- Filter accordion -->
    <div class="filter-accordion">
      {#if type === AssetType.Track || type === AssetType.SFX}
        {#if moods?.length > 0}
          <div class="filter-group">
            <button class="filter-header" on:click={() => expand('moods')} class:expanded={expanded === 'moods'}>
              <span class="filter-title">
                {m['common.moods']()}
                {#if selectedMoods?.length > 0}
                  <span class="count">{selectedMoods.length}</span>
                {/if}
              </span>
              <div class="header-actions">
                {#if selectedMoods?.length > 0}
                  <div
                    class="clear-filter-btn"
                    on:click|stopPropagation={() => clearFilterType('moods')}
                    on:keydown|stopPropagation={(e) => e.key === 'Enter' && clearFilterType('moods')}
                    aria-label={m['actions.clear']()}
                    role="button"
                    tabindex="0"
                  >
                    {m['actions.clear']()}
                  </div>
                {/if}
                <img src={collapseIcon} alt="" loading="lazy" aria-hidden="true" class="expand-icon" />
              </div>
            </button>
            {#if expanded === 'moods'}
              <div class="filter-content" transition:slide>
                <CategoryFilterGroup categories={moods} type={CategoryType.Mood} link={showFiltersAsLinks} on:change={onFilterChanged} />
              </div>
            {/if}
          </div>
        {/if}

        {#if genres?.length > 0}
          <div class="filter-group">
            <button class="filter-header" on:click={() => expand('genres')} class:expanded={expanded === 'genres'}>
              <span class="filter-title">
                {m['common.genres']()}
                {#if selectedGenres?.length > 0}
                  <span class="count">{selectedGenres.length}</span>
                {/if}
              </span>
              <div class="header-actions">
                {#if selectedGenres?.length > 0}
                  <div
                    class="clear-filter-btn"
                    on:click|stopPropagation={() => clearFilterType('genres')}
                    on:keydown|stopPropagation={(e) => e.key === 'Enter' && clearFilterType('genres')}
                    role="button"
                    tabindex="0"
                  >
                    {m['actions.clear']()}
                  </div>
                {/if}
                <img src={collapseIcon} alt="" loading="lazy" aria-hidden="true" class="expand-icon" />
              </div>
            </button>
            {#if expanded === 'genres'}
              <div class="filter-content" transition:slide>
                <CategoryFilterGroup categories={genres} type={CategoryType.Genre} link={showFiltersAsLinks} on:change={onFilterChanged} />
              </div>
            {/if}
          </div>
        {/if}

        {#if energyLevels?.length > 0}
          <div class="filter-group">
            <button class="filter-header" on:click={() => expand('energy-levels')} class:expanded={expanded === 'energy-levels'}>
              <span class="filter-title">
                {m['common.energy-levels']()}
                {#if selectedEnergyLevels?.length > 0}
                  <span class="count">{selectedEnergyLevels.length}</span>
                {/if}
              </span>
              <div class="header-actions">
                {#if selectedEnergyLevels?.length > 0}
                  <div
                    class="clear-filter-btn"
                    on:click|stopPropagation={() => clearFilterType('energyLevels')}
                    on:keydown|stopPropagation={(e) => e.key === 'Enter' && clearFilterType('energyLevels')}
                    role="button"
                    tabindex="0"
                  >
                    {m['actions.clear']()}
                  </div>
                {/if}
                <img src={collapseIcon} alt="" loading="lazy" aria-hidden="true" class="expand-icon" />
              </div>
            </button>
            {#if expanded === 'energy-levels'}
              <div class="filter-content" transition:slide>
                <CategoryFilterGroup
                  categories={energyLevels}
                  type={CategoryType.EnergyLevel}
                  link={showFiltersAsLinks}
                  on:change={onFilterChanged}
                />
              </div>
            {/if}
          </div>
        {/if}

        {#if instruments?.length > 0}
          <div class="filter-group">
            <button class="filter-header" on:click={() => expand('instruments')} class:expanded={expanded === 'instruments'}>
              <span class="filter-title">
                {m['common.instruments']()}
                {#if selectedInstruments?.length > 0}
                  <span class="count">{selectedInstruments.length}</span>
                {/if}
              </span>
              <div class="header-actions">
                {#if selectedInstruments?.length > 0}
                  <div
                    class="clear-filter-btn"
                    on:click|stopPropagation={() => clearFilterType('instruments')}
                    on:keydown|stopPropagation={(e) => e.key === 'Enter' && clearFilterType('instruments')}
                    role="button"
                    tabindex="0"
                  >
                    {m['actions.clear']()}
                  </div>
                {/if}
                <img src={collapseIcon} alt="" loading="lazy" aria-hidden="true" class="expand-icon" />
              </div>
            </button>
            {#if expanded === 'instruments'}
              <div class="filter-content" transition:slide>
                <CategoryFilterGroup
                  categories={instruments}
                  type={CategoryType.Instrument}
                  link={showFiltersAsLinks}
                  on:change={onFilterChanged}
                />
              </div>
            {/if}
          </div>
        {/if}

        {#if themes?.length > 0}
          <div class="filter-group">
            <button class="filter-header" on:click={() => expand('themes')} class:expanded={expanded === 'themes'}>
              <span class="filter-title">
                {m['common.themes']()}
                {#if selectedThemes?.length > 0}
                  <span class="count">{selectedThemes.length}</span>
                {/if}
              </span>
              <div class="header-actions">
                {#if selectedThemes?.length > 0}
                  <div
                    class="clear-filter-btn"
                    on:click|stopPropagation={() => clearFilterType('themes')}
                    on:keydown|stopPropagation={(e) => e.key === 'Enter' && clearFilterType('themes')}
                    role="button"
                    tabindex="0"
                  >
                    {m['actions.clear']()}
                  </div>
                {/if}
                <img src={collapseIcon} alt="" loading="lazy" aria-hidden="true" class="expand-icon" />
              </div>
            </button>
            {#if expanded === 'themes'}
              <div class="filter-content" transition:slide>
                <CategoryFilterGroup categories={themes} type={CategoryType.Theme} link={showFiltersAsLinks} on:change={onFilterChanged} />
              </div>
            {/if}
          </div>
        {/if}

        {#if types?.length > 0}
          <div class="filter-group">
            <button class="filter-header" on:click={() => expand('types')} class:expanded={expanded === 'types'}>
              <span class="filter-title">
                {m['common.types']()}
                {#if selectedTypes?.length > 0}
                  <span class="count">{selectedTypes.length}</span>
                {/if}
              </span>
              <div class="header-actions">
                {#if selectedTypes?.length > 0}
                  <div
                    class="clear-filter-btn"
                    on:click|stopPropagation={() => clearFilterType('types')}
                    on:keydown|stopPropagation={(e) => e.key === 'Enter' && clearFilterType('types')}
                    role="button"
                    tabindex="0"
                  >
                    {m['actions.clear']()}
                  </div>
                {/if}
                <img src={collapseIcon} alt="" loading="lazy" aria-hidden="true" class="expand-icon" />
              </div>
            </button>
            {#if expanded === 'types'}
              <div class="filter-content" transition:slide>
                <CategoryFilterGroup categories={types} type={CategoryType.Type} link={showFiltersAsLinks} on:change={onFilterChanged} />
              </div>
            {/if}
          </div>
        {/if}

        {#if categories?.length > 0}
          <div class="filter-group">
            <button class="filter-header" on:click={() => expand('categories')} class:expanded={expanded === 'categories'}>
              <span class="filter-title">
                {m['common.categories']()}
                {#if selectedCategories?.length > 0}
                  <span class="count">{selectedCategories.length}</span>
                {/if}
              </span>
              <div class="header-actions">
                {#if selectedCategories?.length > 0}
                  <div
                    class="clear-filter-btn"
                    on:click|stopPropagation={() => clearFilterType('categories')}
                    on:keydown|stopPropagation={(e) => e.key === 'Enter' && clearFilterType('categories')}
                    role="button"
                    tabindex="0"
                  >
                    {m['actions.clear']()}
                  </div>
                {/if}
                <img src={collapseIcon} alt="" loading="lazy" aria-hidden="true" class="expand-icon" />
              </div>
            </button>
            {#if expanded === 'categories'}
              <div class="filter-content" transition:slide>
                <CategoryFilterGroup {categories} type={CategoryType.SfxCategory} link={showFiltersAsLinks} on:change={onFilterChanged} />
              </div>
            {/if}
          </div>
        {/if}

        {#if maxBpm > 0 && showBpm}
          <div class="filter-group">
            <button class="filter-header" on:click={() => expand('bpm')} class:expanded={expanded === 'bpm'}>
              <span class="filter-title">
                {m['common.bpm']()}
                {#if selectedMinBpm !== minBpm || selectedMaxBpm !== maxBpm}
                  <span class="count">1</span>
                {/if}
              </span>
              <div class="header-actions">
                {#if selectedMinBpm !== minBpm || selectedMaxBpm !== maxBpm}
                  <div
                    class="clear-filter-btn"
                    on:click|stopPropagation={() => removeBpm()}
                    on:keydown|stopPropagation={(e) => e.key === 'Enter' && removeBpm()}
                    role="button"
                    tabindex="0"
                  >
                    {m['actions.clear']()}
                  </div>
                {/if}
                <img src={collapseIcon} alt="" loading="lazy" aria-hidden="true" class="expand-icon" />
              </div>
            </button>
            {#if expanded === 'bpm'}
              <div class="filter-content range-content" transition:slide>
                <DoubleRangeSlider
                  min={minBpm}
                  max={maxBpm}
                  currentMin={selectedMinBpm}
                  currentMax={selectedMaxBpm}
                  on:change={(e: any) => selectBpm(e.detail.min, e.detail.max)}
                />
              </div>
            {/if}
          </div>
        {/if}

        {#if maxLength > 0 && showLength}
          <div class="filter-group">
            <button class="filter-header" on:click={() => expand('length')} class:expanded={expanded === 'length'}>
              <span class="filter-title">
                {m['common.duration']()}
                {#if selectedMinLength !== minLength || selectedMaxLength !== maxLength}
                  <span class="count">1</span>
                {/if}
              </span>
              <div class="header-actions">
                {#if selectedMinLength !== minLength || selectedMaxLength !== maxLength}
                  <div
                    class="clear-filter-btn"
                    on:click|stopPropagation={() => removeLength()}
                    on:keydown|stopPropagation={(e) => e.key === 'Enter' && removeLength()}
                    role="button"
                    tabindex="0"
                  >
                    {m['actions.clear']()}
                  </div>
                {/if}
                <img src={collapseIcon} alt="" loading="lazy" aria-hidden="true" class="expand-icon" />
              </div>
            </button>
            {#if expanded === 'length'}
              <div class="filter-content range-content" transition:slide>
                <DoubleRangeSlider
                  min={minLength}
                  max={maxLength}
                  currentMin={selectedMinLength}
                  currentMax={selectedMaxLength}
                  format={(value: any) => value + ':00'}
                  on:change={(e: any) => selectLength(e.detail.min, e.detail.max)}
                />
              </div>
            {/if}
          </div>
        {/if}
      {/if}
    </div>
  </div>
</aside>

<!-- Mobile bottom sheets -->
{#if showFiltersSheet}
  <BottomSheet
    tall={true}
    title={m['search.filters']()}
    on:mainAction={closeFiltersSheet}
    on:headerAction={removeAllFilters}
    on:closed={closeFiltersSheet}
  >
    {#if filtering || query}
      <div class="sheet-selected">
        <SearchSelectedFilters
          selectedCategories={allSelectedTags}
          {minBpm}
          {maxBpm}
          {minLength}
          {maxLength}
          {selectedMinBpm}
          {selectedMaxBpm}
          {selectedMinLength}
          {selectedMaxLength}
          {selectedSimilarTo}
          {query}
          on:categoryChange={onFilterChanged}
          on:removeBpm={removeBpm}
          on:removeLength={removeLength}
          on:removeQuery={removeQuery}
          on:removeSimilarTo={removeSimilarTo}
        />
      </div>
    {/if}
    <div class="sheet-list">
      {#if moods?.length > 0}
        <button on:click={() => expand('moods')} class:selected={expanded === 'moods'}>
          {m['common.moods']()}
          {#if selectedMoods?.length > 0}
            ({selectedMoods.length})
          {/if}
          <img src={collapseIcon} alt="" aria-hidden="true" loading="lazy" />
        </button>
        {#if expanded === 'moods'}
          <div class="sheet-expanded" in:slide out:slide>
            <CategoryFilterGroup categories={moods} type={CategoryType.Mood} link={showFiltersAsLinks} on:change={onFilterChanged} />
          </div>
        {/if}
      {/if}
      {#if genres?.length > 0}
        <button on:click={() => expand('genres')} class:selected={expanded === 'genres'}>
          {m['common.genres']()}
          {#if selectedGenres?.length > 0}
            ({selectedGenres.length})
          {/if}
          <img src={collapseIcon} alt="" aria-hidden="true" loading="lazy" />
        </button>
        {#if expanded === 'genres'}
          <div class="sheet-expanded" in:slide out:slide>
            <CategoryFilterGroup categories={genres} type={CategoryType.Genre} link={showFiltersAsLinks} on:change={onFilterChanged} />
          </div>
        {/if}
      {/if}
      {#if energyLevels?.length > 0}
        <button on:click={() => expand('energy-levels')} class:selected={expanded === 'energy-levels'}>
          {m['common.energy-levels']()}
          {#if selectedEnergyLevels?.length > 0}
            ({selectedEnergyLevels.length})
          {/if}
          <img src={collapseIcon} alt="" aria-hidden="true" loading="lazy" />
        </button>
        {#if expanded === 'energy-levels'}
          <div class="sheet-expanded" in:slide out:slide>
            <CategoryFilterGroup
              categories={energyLevels}
              type={CategoryType.EnergyLevel}
              link={showFiltersAsLinks}
              on:change={onFilterChanged}
            />
          </div>
        {/if}
      {/if}
      {#if instruments?.length > 0}
        <button on:click={() => expand('instruments')} class:selected={expanded === 'instruments'}>
          {m['common.instruments']()}
          {#if selectedInstruments?.length > 0}
            ({selectedInstruments.length})
          {/if}
          <img src={collapseIcon} alt="" aria-hidden="true" loading="lazy" />
        </button>
        {#if expanded === 'instruments'}
          <div class="sheet-expanded" in:slide out:slide>
            <CategoryFilterGroup
              categories={instruments}
              type={CategoryType.Instrument}
              link={showFiltersAsLinks}
              on:change={onFilterChanged}
            />
          </div>
        {/if}
      {/if}
      {#if themes?.length > 0}
        <button on:click={() => expand('themes')} class:selected={expanded === 'themes'}>
          {m['common.themes']()}
          {#if selectedThemes?.length > 0}
            ({selectedThemes.length})
          {/if}
          <img src={collapseIcon} alt="" aria-hidden="true" loading="lazy" />
        </button>
        {#if expanded === 'themes'}
          <div class="sheet-expanded" in:slide out:slide>
            <CategoryFilterGroup categories={themes} type={CategoryType.Theme} link={showFiltersAsLinks} on:change={onFilterChanged} />
          </div>
        {/if}
      {/if}
      {#if types?.length > 0}
        <button on:click={() => expand('types')} class:selected={expanded === 'types'}>
          {m['common.types']()}
          {#if selectedTypes?.length > 0}
            ({selectedTypes.length})
          {/if}
          <img src={collapseIcon} alt="" aria-hidden="true" loading="lazy" />
        </button>
        {#if expanded === 'types'}
          <div class="sheet-expanded" in:slide out:slide>
            <CategoryFilterGroup categories={types} type={CategoryType.Type} link={showFiltersAsLinks} on:change={onFilterChanged} />
          </div>
        {/if}
      {/if}
      {#if categories?.length > 0}
        <button on:click={() => expand('categories')} class:selected={expanded === 'categories'}>
          {m['common.categories']()}
          {#if selectedCategories?.length > 0}
            ({selectedCategories.length})
          {/if}
          <img src={collapseIcon} alt="" aria-hidden="true" loading="lazy" />
        </button>
        {#if expanded === 'categories'}
          <div class="sheet-expanded" in:slide out:slide>
            <CategoryFilterGroup {categories} type={CategoryType.SfxCategory} link={showFiltersAsLinks} on:change={onFilterChanged} />
          </div>
        {/if}
      {/if}
      {#if maxBpm > 0 && showBpm}
        <button on:click={(e) => expand('bpm')} class:selected={expanded === 'bpm'}>
          {m['common.bpm']()}
          <img src={collapseIcon} alt="" aria-hidden="true" loading="lazy" />
        </button>
        {#if expanded === 'bpm'}
          <div class="sheet-expanded extra-padding" in:slide out:slide>
            <DoubleRangeSlider
              min={minBpm}
              max={maxBpm}
              currentMin={selectedMinBpm}
              currentMax={selectedMaxBpm}
              on:change={(e: any) => selectBpm(e.detail.min, e.detail.max)}
            />
          </div>
        {/if}
      {/if}
      {#if maxLength > 0 && showLength}
        <button on:click={(e) => expand('length')} class:selected={expanded == 'length'}>
          {m['common.duration']()}
          <img src={collapseIcon} alt="" aria-hidden="true" loading="lazy" />
        </button>
        {#if expanded === 'length'}
          <div class="sheet-expanded extra-padding" in:slide out:slide>
            <DoubleRangeSlider
              min={minLength}
              max={maxLength}
              currentMin={selectedMinLength}
              currentMax={selectedMaxLength}
              format={(value: any) => value + ':00'}
              on:change={(e: any) => selectLength(e.detail.min, e.detail.max)}
            />
          </div>
        {/if}
      {/if}
    </div>
  </BottomSheet>
{/if}

<style>
  .sidebar {
    width: 252px;
    height: calc(100vh - 129px);
    position: fixed;
    left: 0;
    top: var(--header-height-desktop-search);
    background: #fcfcfd;
    border-right: 1px solid #f0f0f0;
    overflow-y: auto;
    z-index: 10;
    display: flex;
    flex-direction: column;
  }

  .filters-section {
    flex: 1;
    padding: 16px;
    padding-bottom: 100px;
  }

  .filters-section h3 {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 16px 0;
  }

  .filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-top: 6px;
  }

  .filters-header h3 {
    color: var(--text-primary);
    margin: 0;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0%;
  }

  .filters-header .total-count {
    font-size: 16px;
    font-weight: 600;
    color: var(--text-invert-secondary);
  }

  .clear-all-btn {
    cursor: pointer;
    transition: all 0.2s ease;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0%;
    text-decoration: underline;
    text-decoration-style: solid;
    text-decoration-thickness: 8%;
    text-underline-offset: 3px;
    text-decoration-skip-ink: auto;
  }

  .clear-all-btn:hover {
    color: var(--charleston-black);
  }

  .filter-accordion {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .filter-group {
    overflow: hidden;
  }

  .filter-header {
    width: 100%;
    padding-top: 18px;
    padding-bottom: 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    color: var(--text-secondary);
    text-align: left;
    transition: color 0.2s ease;
    border-bottom: 1px solid var(--cultured);
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
  }

  .filter-header:hover {
    color: var(--charleston-black);
  }

  .filter-header.expanded {
    color: var(--text-primary);
    border-bottom: 1px solid transparent;
  }

  .filter-header .expand-icon {
    width: 16px;
    height: 16px;
    object-fit: contain;
    transition: transform 0.2s ease;
  }

  .filter-header.expanded .expand-icon {
    transform: rotate(180deg);
  }

  .filter-header .filter-title {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .filter-header .count {
    color: var(--text-primary);
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
    background: #e6e8ea;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .filter-header .header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .filter-header .clear-filter-btn {
    cursor: pointer;
    transition: background 0.2s ease;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0%;
    text-decoration: underline;
    text-decoration-style: solid;
    text-decoration-thickness: 8%;
    text-underline-offset: 3px;
    text-decoration-skip-ink: auto;
    color: var(--text-primary);
  }

  .filter-header .clear-filter-btn:hover {
    color: var(--charleston-black);
  }

  .filter-content {
    border-bottom: 1px solid var(--cultured);
  }

  .range-content {
    padding: 20px 16px;
  }

  .sheet-list {
    display: flex;
    flex-direction: column;
  }

  .sheet-list button {
    height: 52px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--cultured);
    margin-left: 16px;
    margin-right: 16px;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: var(--text-secondary);
  }

  .sheet-list button img {
    width: 20px;
    height: 20px;
    object-fit: contain;
    aspect-ratio: 1;
    opacity: 0.5;
  }

  .sheet-list button.selected {
    color: var(--text-primary);
    border-bottom-color: transparent;
  }

  .sheet-list button.selected img {
    opacity: 1;
    transform: rotate(180deg);
  }

  .sheet-selected {
    padding: 0 16px;
  }

  .sheet-expanded {
    padding: 16px;
    border-bottom: 1px solid var(--cultured);
  }

  .sheet-expanded.extra-padding {
    padding-top: 30px;
  }

  @media (max-width: 1050px) {
    .sidebar {
      display: none;
    }
  }
</style>
